/**
 * A document type whose CMS folder carries a node named "PDF form" produces
 * its document by filling an official PDF form instead of rendering the
 * folder's Layout and Pages as HTML.
 *
 * The node's content is the file name of the blank form under public/forms
 * — for the lender questionnaire, `fannie-mae-1076-2021-12.pdf`. That keeps
 * Content Management as the place that answers "which template does this
 * bank use": a bank with its own fillable PDF is another folder with another
 * "PDF form" node, and a bank with a format that cannot be filled keeps using
 * the HTML path.
 */

import { config } from "@/config";

export interface PdfFormTemplate {
  /** The CMS node that declared it. */
  nodeId: number;
  /** File name under public/forms. */
  file: string;
  /** Where the browser fetches the blank form from. */
  url: string;
}

const NODE_NAME = "pdf form";
const FILE_NAME = /^[\w.-]+\.pdf$/i;

/** The first line of text in an HTML fragment, tags and entities removed. */
function firstLine(html: string): string {
  const text = html
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/(p|div|li|h\d)>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"');
  return text
    .split("\n")
    .map((line) => line.trim())
    .find((line) => line !== "") ?? "";
}

export function pdfFormUrl(file: string): string {
  const base = (import.meta as any).env?.BASE_URL ?? "/";
  return `${base.endsWith("/") ? base : base + "/"}forms/${file}`;
}

/**
 * Looks for a "PDF form" node directly under the document type's template
 * folder. Returns null when the folder is an ordinary HTML template.
 */
export async function findPdfFormTemplate(
  templateId: number | null | undefined,
): Promise<PdfFormTemplate | null> {
  if (!templateId) return null;

  try {
    const treeResponse = await fetch(`${config.SUPABASE_URL}/functions/v1/cms-content/tree`);
    if (!treeResponse.ok) return null;
    const tree: any[] = await treeResponse.json();

    const node = tree.find(
      (item) =>
        item.parent_id === templateId &&
        String(item.name ?? "").trim().toLowerCase() === NODE_NAME,
    );
    if (!node) return null;

    const translationsResponse = await fetch(
      `${config.SUPABASE_URL}/functions/v1/cms-content/${node.id}/translations`,
    );
    if (!translationsResponse.ok) {
      throw new Error(`The "PDF form" node (${node.id}) has no readable content`);
    }
    const translations: any[] = await translationsResponse.json();
    const translation =
      translations.find((t) => t.language_code === "en") ?? translations[0];
    const file = firstLine(translation?.html_content || translation?.content || "");

    if (!FILE_NAME.test(file)) {
      throw new Error(
        `The "PDF form" node (${node.id}) must contain the file name of a PDF under public/forms; it says "${file || "nothing"}"`,
      );
    }

    return { nodeId: node.id, file, url: pdfFormUrl(file) };
  } catch (error) {
    // A folder that declares a PDF form but declares it badly must not fall
    // back to the HTML path silently: the operator would get the wrong
    // document and not know why.
    if (error instanceof Error && error.message.includes('"PDF form" node')) throw error;
    console.error("[pdfFormTemplate] Could not read the template folder:", error);
    return null;
  }
}
